import React, { useEffect, useState, useContext, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCartShopping, faPlusCircle, faMinusCircle,
  faTrashCan, faAddressCard, faBars, faXmark,
} from '@fortawesome/free-solid-svg-icons'
import axios from 'axios'
import Swal from 'sweetalert2'
import jwt_decode from 'jwt-decode'
import { useTranslation } from 'react-i18next'
import CartContext from '../../../middlewares/cart/CartContext'
import AuthContext from '../../../middlewares/AuthContext'

// ── Types ─────────────────────────────────────────────────────────────────────
interface CartProduct {
  product_name: string
  name: string
  description: string
}

interface CartItem {
  product: CartProduct
  quantity: number
}

interface AuthCtx {
  onLogout: () => void
  token: string | null
  getHeaders: () => Promise<Record<string, string>>
}

interface CartCtx {
  cart: CartItem[]
  countItem: (item: CartItem, delta: number) => void
  deleteItem: (item: CartItem) => void
}

// ── Constants ─────────────────────────────────────────────────────────────────
const BRAND = '#6eb446'

const btnReset: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '8px',
  borderRadius: '8px',
  color: '#374151',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

// ── Hooks ─────────────────────────────────────────────────────────────────────
function useClickOutside(ref: React.RefObject<HTMLElement | null>, cb: () => void) {
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) cb()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [ref, cb])
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpoint)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [breakpoint])
  return isMobile
}

// ── Dropdown ──────────────────────────────────────────────────────────────────
interface DropdownProps {
  open: boolean
  onClose: () => void
  align?: 'left' | 'right'
  width?: number
  children: React.ReactNode
}

const Dropdown: React.FC<DropdownProps> = ({ open, onClose, align = 'left', width = 200, children }) => {
  const ref = useRef<HTMLDivElement>(null)
  useClickOutside(ref, onClose)

  if (!open) return null
  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        top: 'calc(100% + 8px)',
        [align]: 0,
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
        border: '1px solid #f1f5f9',
        width,
        padding: '6px',
        zIndex: 200,
      }}
    >
      {children}
    </div>
  )
}

// ── HomeNav ───────────────────────────────────────────────────────────────────
const HomeNav: React.FC = () => {
  const { onLogout, token, getHeaders } = useContext(AuthContext) as AuthCtx
  const { cart, countItem, deleteItem } = useContext(CartContext) as CartCtx
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  const url = process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_URL
    : process.env.REACT_APP_URL_DEV

  const [tokenDecode, setTokenDecode] = useState<any>(null)
  const [menuOpen, setMenuOpen]       = useState(false)
  const [cartOpen, setCartOpen]       = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)
  const [langOpen, setLangOpen]       = useState(false)

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const validate = async () => {
      if (token === null) return
      try {
        const headers = await getHeaders()
        await axios.get(`${url}auth/validate`, { headers })
      } catch (_) {
        onLogout()
      }
    }

    if (token !== null) setTokenDecode(jwt_decode(token))
    validate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Handlers ──────────────────────────────────────────────────────────────
  const scrollToProducts = (e: React.MouseEvent) => {
    e.preventDefault()
    setMenuOpen(false)
    document.getElementById('products-section')?.scrollIntoView({ behavior: 'smooth' })
  }

  const confirmDeleteItem = (item: CartItem) => {
    Swal.fire({
      title: t('header.cart.delete_item'),
      text: t('header.cart.do_you'),
      showCancelButton: true,
      confirmButtonText: t('header.cart.delete'),
      customClass: { confirmButton: 'btn btn-outline-danger', cancelButton: 'btn btn-primary mx-2' },
      reverseButtons: true,
      buttonsStyling: false,
      position: 'top',
      showClass: { popup: 'animate__animated animate__fadeInDown' },
      hideClass: { popup: 'animate__animated animate__fadeOutUp' },
    }).then(({ isConfirmed }) => { if (isConfirmed) deleteItem(item) })
  }

  const logout = () => {
    Swal.fire({
      title: t('header.logout_session'),
      showCancelButton: true,
      confirmButtonColor: BRAND,
      cancelButtonColor: '#ef4444',
      confirmButtonText: t('header.yes_logout_session'),
      showClass: { popup: 'animate__animated animate__fadeInDown' },
      hideClass: { popup: 'animate__animated animate__fadeOutUp' },
    }).then(({ isConfirmed }) => {
      if (isConfirmed) { onLogout(); navigate('/') }
    })
  }

  const totalItems = cart.reduce((acc, item) => acc + item.quantity, 0)

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <header style={{
      position: 'sticky',
      top: 0,
      zIndex: 100,
      backgroundColor: '#fff',
      borderBottom: '1px solid #e2e8f0',
      boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
    }}>
      {/* QA banner */}
      {process.env.REACT_APP_ENV === 'qa' && (
        <div style={{
          backgroundColor: '#ef4444', color: '#fff',
          fontWeight: 700, fontSize: '0.75rem',
          textAlign: 'center', padding: '4px',
        }}>
          Entorno de QA
        </div>
      )}

      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 1.5rem' }}>
        {/* ── Main row ── */}
        <div style={{ display: 'flex', alignItems: 'center', height: 64 }}>

          {/* Left — Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
            <img
              src="https://labeldictate.com/assets/img/Label-Dictate-Logo-V2021.png"
              width={84}
              alt="Label Dictate"
            />
          </Link>

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Right — Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>

            {/* Products link (desktop only) */}
            {!isMobile && (
              <a
                href="#products-section"
                onClick={scrollToProducts}
                style={{
                  padding: '7px 16px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: 650,
                  fontSize: '0.9rem',
                  color: '#374151',
                  transition: 'color 0.15s, background 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = BRAND; e.currentTarget.style.background = '#f4fbf0' }}
                onMouseLeave={e => { e.currentTarget.style.color = '#374151'; e.currentTarget.style.background = 'transparent' }}
              >
                {t('header.products')}
              </a>
            )}

            {/* Cart */}
            <div style={{ position: 'relative' }}>
              <button onClick={() => setCartOpen(v => !v)} style={{ ...btnReset, position: 'relative' }} aria-label="Cart">
                <FontAwesomeIcon icon={faCartShopping} style={{ fontSize: '1.1rem' }} />
                {totalItems > 0 && (
                  <span style={{
                    position: 'absolute', top: 2, right: 2,
                    backgroundColor: BRAND, color: '#fff',
                    borderRadius: '50%', width: 16, height: 16,
                    fontSize: '0.6rem', fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {totalItems}
                  </span>
                )}
              </button>

              <Dropdown open={cartOpen} onClose={() => setCartOpen(false)} align="right" width={320}>
                {cart.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#94a3b8', padding: '16px', margin: 0, fontSize: '0.875rem' }}>
                    {t('header.cart.empty')}
                  </p>
                ) : (
                  <>
                    <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                      {cart.map((item, i) => (
                        <div key={i} style={{ padding: '10px', borderBottom: '1px solid #f1f5f9' }}>
                          <p style={{ fontWeight: 600, margin: '0 0 2px', fontSize: '0.83rem', color: '#1e293b' }}>
                            {item.product.product_name}
                          </p>
                          <p style={{ margin: '0 0 8px', fontSize: '0.76rem', color: '#64748b' }}>
                            {item.product.name} — {item.product.description.substring(0, 28)}…
                          </p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <button
                              disabled={item.quantity <= 1}
                              onClick={() => countItem(item, -1)}
                              style={{ ...btnReset, padding: 4, opacity: item.quantity <= 1 ? 0.3 : 1, cursor: item.quantity <= 1 ? 'not-allowed' : 'pointer' }}
                            >
                              <FontAwesomeIcon icon={faMinusCircle} color="#ef4444" />
                            </button>
                            <span style={{ fontWeight: 600, fontSize: '0.875rem', minWidth: 20, textAlign: 'center' }}>
                              {item.quantity}
                            </span>
                            <button
                              disabled={item.quantity >= 10}
                              onClick={() => countItem(item, 1)}
                              style={{ ...btnReset, padding: 4, opacity: item.quantity >= 10 ? 0.3 : 1, cursor: item.quantity >= 10 ? 'not-allowed' : 'pointer' }}
                            >
                              <FontAwesomeIcon icon={faPlusCircle} color={BRAND} />
                            </button>
                            <button
                              onClick={() => confirmDeleteItem(item)}
                              style={{ ...btnReset, padding: 4, marginLeft: 'auto' }}
                            >
                              <FontAwesomeIcon icon={faTrashCan} color="#94a3b8" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ padding: '10px' }}>
                      <button
                        onClick={() => { navigate('/checkout'); setCartOpen(false) }}
                        style={{
                          width: '100%', padding: '10px', borderRadius: '8px',
                          border: 'none', backgroundColor: BRAND, color: '#fff',
                          fontWeight: 700, cursor: 'pointer', fontSize: '0.875rem',
                        }}
                      >
                        {t('header.cart.gotocheckout')}
                      </button>
                    </div>
                  </>
                )}
              </Dropdown>
            </div>

            {/* Login / Account (desktop only) */}
            {!isMobile && (
              token === null ? (
                <Link
                  to="/auth/login"
                  style={{
                    display: 'inline-flex', alignItems: 'center',
                    padding: '7px 18px',
                    borderRadius: '9999px',
                    border: `1.5px solid ${BRAND}`,
                    backgroundColor: BRAND,
                    color: '#fff',
                    fontWeight: 600, fontSize: '0.82rem',
                    textDecoration: 'none',
                    letterSpacing: '0.01em',
                    boxShadow: '0 4px 14px rgba(110,180,70,0.3)',
                    transition: 'filter 0.15s, transform 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.08)'; e.currentTarget.style.transform = 'translateY(-1px)' }}
                  onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.transform = 'translateY(0)' }}
                >
                  {t('header.login')}
                </Link>
              ) : (
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setAccountOpen(v => !v)}
                    style={{
                      ...btnReset, gap: 6, padding: '7px 14px',
                      border: '1.5px solid #e2e8f0', backgroundColor: '#f8fafc',
                      borderRadius: '9999px',
                      fontWeight: 600, fontSize: '0.82rem',
                      letterSpacing: '0.01em',
                      transition: 'background 0.15s, border-color 0.15s, color 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f4fbf0'; e.currentTarget.style.borderColor = BRAND; e.currentTarget.style.color = BRAND }}
                    onMouseLeave={e => { if (!accountOpen) { e.currentTarget.style.backgroundColor = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#374151' } }}
                  >
                    <FontAwesomeIcon icon={faAddressCard} />
                    {t('header.my_account')}
                  </button>
                  <Dropdown open={accountOpen} onClose={() => setAccountOpen(false)} align="right" width={230}>
                    <p style={{ padding: '8px 10px', margin: 0, fontSize: '0.78rem', color: '#64748b' }}>
                      {tokenDecode?.email || '—'}
                    </p>
                    <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #f1f5f9' }} />
                    <Link
                      to="dashboard"
                      onClick={() => setAccountOpen(false)}
                      style={{ display: 'block', padding: '8px 10px', borderRadius: '8px', textDecoration: 'none', color: '#374151', fontSize: '0.875rem' }}
                    >
                      Portal Labeldictate
                    </Link>
                    <button
                      onClick={() => { logout(); setAccountOpen(false) }}
                      style={{ ...btnReset, width: '100%', justifyContent: 'flex-start', padding: '8px 10px', color: '#ef4444', fontSize: '0.875rem' }}
                    >
                      {t('header.logout')}
                    </button>
                  </Dropdown>
                </div>
              )
            )}

            {/* Language (desktop only) */}
            {!isMobile && (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setLangOpen(v => !v)}
                  style={{
                    ...btnReset,
                    gap: 7,
                    fontWeight: 600,
                    fontSize: '0.82rem',
                    padding: '7px 14px',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '9999px',
                    backgroundColor: langOpen ? '#f4fbf0' : '#f8fafc',
                    color: langOpen ? BRAND : '#374151',
                    transition: 'background 0.15s, border-color 0.15s, color 0.15s',
                    letterSpacing: '0.01em',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f4fbf0'; e.currentTarget.style.borderColor = BRAND; e.currentTarget.style.color = BRAND }}
                  onMouseLeave={e => { if (!langOpen) { e.currentTarget.style.backgroundColor = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#374151' } }}
                >
                  <span className={`fi fi-${i18n.language === 'en' ? 'us' : 'mx'}`} />
                  {t('header.language')}
                  <span style={{ fontSize: '0.55rem', opacity: 0.6, marginLeft: 2, transition: 'transform 0.2s', display: 'inline-block', transform: langOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                </button>
                <Dropdown open={langOpen} onClose={() => setLangOpen(false)} align="right" width={140}>
                  {[
                    { code: 'en', flag: 'us', label: 'English' },
                    { code: 'es', flag: 'mx', label: 'Español' },
                  ].map(({ code, flag, label }) => (
                    <button
                      key={code}
                      onClick={() => { i18n.changeLanguage(code); setLangOpen(false) }}
                      style={{
                        ...btnReset, width: '100%', justifyContent: 'flex-start',
                        gap: 8, padding: '8px 10px', fontSize: '0.875rem', color: '#374151',
                        fontWeight: i18n.language === code ? 600 : 400,
                        backgroundColor: i18n.language === code ? '#f0f9ea' : 'transparent',
                        borderRadius: '8px',
                      }}
                    >
                      <span className={`fi fi-${flag}`} />
                      {label}
                    </button>
                  ))}
                </Dropdown>
              </div>
            )}

            {/* Hamburger — mobile only */}
            {isMobile && (
              <button onClick={() => setMenuOpen(v => !v)} style={btnReset} aria-label="Menu">
                <FontAwesomeIcon icon={menuOpen ? faXmark : faBars} style={{ fontSize: '1.15rem' }} />
              </button>
            )}
          </div>
        </div>

        {/* ── Mobile menu ── */}
        {isMobile && menuOpen && (
          <div style={{ borderTop: '1px solid #f1f5f9', padding: '12px 0 16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <a
              href="#products-section"
              onClick={scrollToProducts}
              style={{ padding: '10px 8px', borderRadius: '8px', textDecoration: 'none', color: '#374151', fontWeight: 500, fontSize: '0.95rem' }}
            >
              {t('header.products')}
            </a>

            {token === null ? (
              <Link
                to="/auth/login"
                onClick={() => setMenuOpen(false)}
                style={{
                  display: 'block', marginTop: 8, padding: '10px',
                  textAlign: 'center', borderRadius: '8px',
                  border: `1.5px solid ${BRAND}`, color: BRAND,
                  fontWeight: 600, textDecoration: 'none', fontSize: '0.9rem',
                }}
              >
                {t('header.login')}
              </Link>
            ) : (
              <>
                <Link
                  to="dashboard"
                  onClick={() => setMenuOpen(false)}
                  style={{ padding: '10px 8px', borderRadius: '8px', textDecoration: 'none', color: '#374151', fontSize: '0.9rem' }}
                >
                  Portal Labeldictate
                </Link>
                <button
                  onClick={() => { logout(); setMenuOpen(false) }}
                  style={{ ...btnReset, justifyContent: 'flex-start', padding: '10px 8px', color: '#ef4444', fontWeight: 600 }}
                >
                  {t('header.logout')}
                </button>
              </>
            )}

            {/* Language in mobile menu */}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              {[
                { code: 'en', flag: 'us', label: 'English' },
                { code: 'es', flag: 'mx', label: 'Español' },
              ].map(({ code, flag, label }) => (
                <button
                  key={code}
                  onClick={() => { i18n.changeLanguage(code); setMenuOpen(false) }}
                  style={{
                    ...btnReset, gap: 6, padding: '7px 12px',
                    border: `1px solid ${i18n.language === code ? BRAND : '#e2e8f0'}`,
                    borderRadius: '8px', fontSize: '0.875rem',
                    fontWeight: i18n.language === code ? 600 : 400,
                    color: i18n.language === code ? BRAND : '#374151',
                  }}
                >
                  <span className={`fi fi-${flag}`} />
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  )
}

export default HomeNav
